/**
 * Single source of truth for platform-account presentation: plan colors,
 * funnel stages, event labels, tag colors, and money formatting.
 *
 * Every surface that renders platform accounts (AccountsPage list/detail,
 * the contact page's SyncSuitePanel, the Pipelines funnel board) imports
 * from here — a stage or label added in one place shows up everywhere,
 * instead of drifting across three copy-pasted maps (which is exactly how
 * the "trialing" stage briefly rendered blank chips).
 *
 * Stage KEYS mirror the backend derivation (api routes/platform-accounts.ts
 * STAGE_SQL) and never change without it; LABELS are presentation.
 */

export const PLAN_COLORS: Record<string, string> = {
  free: "zinc",
  pro: "indigo",
  agency: "violet",
  mobileapp: "cyan",
};

export const STAGES: { key: string; label: string; color: string }[] = [
  { key: "signed_up", label: "Signed up", color: "zinc" },
  { key: "verified", label: "Verified", color: "cyan" },
  { key: "built", label: "Messaged AI", color: "blue" },
  { key: "published", label: "Published", color: "indigo" },
  { key: "card", label: "Card added", color: "emerald" },
  { key: "domain", label: "Domain live", color: "blue" },
  { key: "trialing", label: "Trial", color: "amber" },
  { key: "beta", label: "Beta", color: "zinc" },
  { key: "customer", label: "Paying", color: "violet" },
  { key: "canceled", label: "Canceled", color: "red" },
  { key: "deleted", label: "Deleted", color: "zinc" },
];

export const STAGE_BY_KEY: Record<string, { key: string; label: string; color: string }> =
  Object.fromEntries(STAGES.map((s) => [s.key, s]));

export const EVENT_LABELS: Record<string, string> = {
  account_created: "Account created",
  logged_in: "Logged in",
  email_verified: "Email verified",
  phone_verified: "Phone verified",
  ai_message: "AI message",
  published: "Published site",
  custom_domain_added: "Custom domain added",
  custom_domain_verified: "Custom domain verified",
  email_domain_connected: "Email domain connected",
  email_domain_verified: "Email domain verified",
  email_sent: "Email sent",
  sms_sent: "SMS sent",
  phone_number_purchased: "Phone number purchased",
  a2p_submitted: "A2P submitted",
  domain_purchased: "Domain purchased",
  team_member_added: "Team member added",
  card_added: "Card added",
  auto_topup_enabled: "Auto top-up enabled",
  auto_topup_disabled: "Auto top-up disabled",
  wallet_topup: "Wallet top-up",
  plan_changed: "Plan changed",
  project_created: "Project created",
  infra_activated: "Workspace activated",
  trial_ending: "Trial ending",
  trial_expired: "Trial expired",
  payment_failed: "Payment failed",
  payment_succeeded: "Payment received",
  subscription_canceled: "Subscription canceled",
  limit_hit: "Hit plan limit",
  wallet_low: "Wallet low",
  spend_blocked: "Spend blocked",
  account_deleted: "Account deleted",
  addon_purchased: "Add-on purchased",
  addon_canceled: "Add-on canceled",
  refund_issued: "Refund issued",
  dispute_opened: "Payment disputed",
  team_member_removed: "Team member removed",
  event_seat_reserved: "AI-Ready Day seat saved",
  event_seat_claimed: "AI-Ready Day date picked",
  event_seat_moved: "AI-Ready Day date changed",
  event_seat_canceled: "AI-Ready Day seat canceled",
  event_join_clicked: "Clicked AI-Ready Day join link",
  event_attended: "Attended AI-Ready Day",
  event_no_show: "Missed AI-Ready Day",
  event_trial_started: "Started trial at AI-Ready Day",
  mastermind_unlocked: "Invited to the Mastermind",
  upgrade_purchased: "Upgrade purchased",
};

export function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/[_.]/g, " ");
}

// Mirrors the tag colors the backend seeds into the tags table
// (api routes/platform-activity.ts EVENT_TAGS) so account chips match the
// contact-tag chips everywhere.
export const TAG_COLORS: Record<string, string> = {
  "Signed Up": "emerald",
  "Logged In": "blue",
  "Email Verified": "cyan",
  "Phone Verified": "cyan",
  "Messaged AI": "violet",
  "Published Site": "indigo",
  "Added Domain": "blue",
  "Domain Verified": "blue",
  "Email Connected": "cyan",
  "Email Domain Verified": "cyan",
  "Sent Email": "amber",
  "Sent SMS": "amber",
  "Has Phone Number": "orange",
  "A2P Submitted": "orange",
  "Bought Domain": "green",
  "Has Team": "pink",
  "Added Card": "emerald",
  "Auto Top-up On": "emerald",
  "Auto Top-up Off": "orange",
  "Topped Up": "green",
  "Changed Plan": "fuchsia",
  "Created Project": "indigo",
  "Activated": "emerald",
  "Trial Ending": "amber",
  "Trial Expired": "amber",
  "Payment Failed": "red",
  "Canceled": "red",
  "Hit Free Limit": "amber",
  "Wallet Low": "amber",
  "Spend Blocked": "orange",
  "Account Deleted": "zinc",
  "Bought Add-on": "green",
  "Refunded": "red",
  "Disputed": "red",
  "AI-Ready Day Reserved": "amber",
  "AI-Ready Day Booked": "indigo",
  "AI-Ready Day Moved": "blue",
  "AI-Ready Day Canceled": "red",
  "AI-Ready Day Clicked Join": "cyan",
  "AI-Ready Day Attended": "emerald",
  "AI-Ready Day No-show": "orange",
  "Trial at AI-Ready Day": "violet",
  "Mastermind Invited": "yellow",
  "Mastermind": "yellow",
  "Done-for-you Month": "green",
  "Done-for-you Reserved": "amber",
};

export function tagColor(tag: string): string {
  if (tag.startsWith("Plan: ")) return "fuchsia";
  return TAG_COLORS[tag] ?? "zinc";
}

/**
 * True for a REAL trial: subscription trialing AND ending within 30 days.
 * Longer "trials" are hand-comped beta accounts — they never render as a
 * conversion countdown (they get a quiet zinc "beta" chip instead).
 */
export function isNearTrial(endsAt: string | null | undefined): boolean {
  if (!endsAt) return false;
  const t = new Date(endsAt).getTime();
  return !Number.isNaN(t) && t - Date.now() <= 30 * 86_400_000;
}

/** "$12.34" for a positive numeric string; null for missing/zero/garbage. */
export function money(v: string | null | undefined): string | null {
  if (v == null) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return null;
  return `$${n.toFixed(2)}`;
}

/**
 * Pipeline value model — 100% derived from REAL data:
 *   prices mirror the platform's plans table (pro $89, agency $397), and
 *   conversion odds are MEASURED from this workspace's own history:
 *   P(pay | reached stage) = payers who passed through that stage ÷
 *   everyone who reached it. Betas (long comp trials) are excluded from
 *   the math entirely. With few payers the odds are noisy by nature —
 *   they sharpen automatically as more customers convert.
 */
export const PLAN_MONTHLY_USD: Record<string, number> = {
  free: 0,
  mobileapp: 0,
  pro: 89,
  agency: 397,
};
const TARGET_PLAN_USD = PLAN_MONTHLY_USD.pro!; // what a free account converts TO

export interface PipelineValueInput {
  stage?: string;
  owner_type?: string | null;
  plan_code?: string | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  revenue_usd?: string | null;
  email_verified_at?: string | null;
  first_ai_at?: string | null;
  first_published_at?: string | null;
  first_domain_live_at?: string | null;
  card_added_at?: string | null;
  trial_started_at?: string | null;
  first_paid_at?: string | null;
}

const isBetaAccount = (a: PipelineValueInput) =>
  a.subscription_status === "trialing" && !isNearTrial(a.trial_ends_at);
const isPayingAccount = (a: PipelineValueInput) => Number(a.revenue_usd) > 0;

const REACHED: Record<string, (a: PipelineValueInput) => boolean> = {
  signed_up: () => true,
  verified: (a) => !!a.email_verified_at,
  built: (a) => !!a.first_ai_at,
  published: (a) => !!a.first_published_at,
  domain: (a) => !!a.first_domain_live_at,
  card: (a) => !!a.card_added_at,
  trialing: (a) => !!(a.trial_started_at || a.first_paid_at),
};

/** Measured P(pay | reached stage) from the account list itself. Stages
 *  nobody has reached yet fall back to the overall signup→pay rate. */
export function computeMeasuredOdds(
  accounts: PipelineValueInput[],
): Record<string, number> {
  const eligible = accounts.filter(
    (a) => !isBetaAccount(a) && a.stage !== "deleted",
  );
  const payers = eligible.filter(isPayingAccount);
  const baseRate = eligible.length > 0 ? payers.length / eligible.length : 0;
  const odds: Record<string, number> = {};
  for (const [stage, reached] of Object.entries(REACHED)) {
    const denom = eligible.filter(reached).length;
    odds[stage] = denom > 0 ? payers.filter(reached).length / denom : baseRate;
  }
  return odds;
}

/**
 * What an account is worth per month, weighted by the MEASURED odds:
 *   paying customer → their real plan price (100%)
 *   beta comp → $0 (a gift, not pipeline)
 *   real trial → measured trial-conversion rate × their plan's price
 *   pre-paying stages → measured stage rate × the Pro price
 */
export function monthlyValueUsd(
  a: PipelineValueInput,
  odds: Record<string, number>,
): { usd: number; weighted: boolean } {
  const stage = a.stage ?? "signed_up";
  if (stage === "canceled" || stage === "deleted" || stage === "beta") {
    return { usd: 0, weighted: false };
  }
  if (stage === "customer") {
    if (isBetaAccount(a)) return { usd: 0, weighted: false };
    // "Collecting" is money-verified: an active-but-never-billed
    // subscription (hand-comped, e.g. a gifted Agency plan) counts $0.
    if (!isPayingAccount(a)) return { usd: 0, weighted: false };
    return { usd: PLAN_MONTHLY_USD[a.plan_code ?? ""] ?? 0, weighted: false };
  }
  if (stage === "trialing") {
    const price = PLAN_MONTHLY_USD[a.plan_code ?? ""] || TARGET_PLAN_USD;
    return { usd: (odds.trialing ?? 0) * price, weighted: true };
  }
  return { usd: (odds[stage] ?? 0) * TARGET_PLAN_USD, weighted: true };
}

/** Lifetime revenue = subscription invoices + wallet top-ups. */
export function lifetimeTotal(a: {
  revenue_usd?: string | null;
  lifetime_spend_usd?: string | null;
}): number {
  return (Number(a.revenue_usd) || 0) + (Number(a.lifetime_spend_usd) || 0);
}
